import { RefreshCw, UserX } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import type { AdminStatus } from '@bccomms/shared';
import { session } from '../../lib/session';
import { useStore } from '../../store';
import { fmtTime } from '../ui';

export function StatusTab() {
  const [status, setStatus] = useState<AdminStatus | null>(null);
  const me = useStore((s) => s.user);
  const refresh = useCallback(async () => {
    try {
      setStatus((await session.admin('admin.status', {})) as AdminStatus);
    } catch (err) {
      console.warn(err);
    }
  }, []);
  useEffect(() => {
    void refresh();
    const t = setInterval(() => void refresh(), 5000);
    return () => clearInterval(t);
  }, [refresh]);

  if (!status) return <p className="muted">Loading…</p>;
  const up = `${Math.floor(status.uptimeSec / 3600)}h ${Math.floor((status.uptimeSec % 3600) / 60)}m`;

  return (
    <>
      <div className="admin__title">
        <h2>Server status</h2>
        <button className="btn btn--ghost" onClick={() => void refresh()}>
          <RefreshCw size={16} /> Refresh
        </button>
      </div>
      <div className="stats">
        {[
          ['Server', status.serverName],
          ['Uptime', up],
          ['Connected', String(status.peers.length)],
          ['Seats in use', status.seats.maxUsers ? `${status.seats.inUse} of ${status.seats.maxUsers}${status.seats.plan ? ` · ${status.seats.plan}` : ''}` : `${status.seats.inUse} · no limit`],
          ['Media address', `${status.announcedIp}:${status.rtcPort}-${status.rtcPort + status.workers.length - 1}`],
          ['mediasoup', status.mediasoupVersion],
          ['Node', status.node],
          ['Memory', `${status.memoryMb} MB`],
          ['Workers', `${status.workers.length}`],
        ].map(([l, v]) => (
          <div key={l} className="card stat">
            <span className="stat__label">{l}</span>
            <span className="stat__value">{v}</span>
          </div>
        ))}
      </div>
      <div className="section-title">Mediasoup workers</div>
      <table className="table" style={{ marginBottom: 24 }}>
        <thead>
          <tr>
            <th>#</th>
            <th>PID</th>
            <th>Port</th>
            <th>Peers</th>
          </tr>
        </thead>
        <tbody>
          {status.workers.map((w) => (
            <tr key={w.index}>
              <td className="mono">{w.index}</td>
              <td className="mono">{w.pid}</td>
              <td className="mono">{w.port}</td>
              <td className="mono">{w.peers}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="section-title">Connected peers</div>
      <table className="table">
        <thead>
          <tr>
            <th>User</th>
            <th>Client</th>
            <th>Since</th>
            <th>Transports</th>
            <th>Producers</th>
            <th>Consumers</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {status.peers.map((p) => (
            <tr key={p.userId}>
              <td className="mono">{p.username}</td>
              <td>{p.client}</td>
              <td className="mono">{fmtTime(p.connectedAt)}</td>
              <td className="mono">{p.transports}</td>
              <td className="mono">{p.producers}</td>
              <td className="mono">{p.consumers}</td>
              <td>
                {p.userId !== me?.id && (
                  <button className="iconbtn" aria-label="Disconnect" title="Disconnect" onClick={() => void session.admin('admin.kick', { userId: p.userId }).then(refresh)}>
                    <UserX size={16} />
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}
